import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import {EquineService} from './equine.service';
import {ActivatedRoute, Router} from '@angular/router';
import {UserInfoService} from '../../services/user-info.service';
import {MatSnackBar} from '@angular/material/snack-bar';
import { normalizeEquineQuotedReferralState, wasEquineQuotePreviouslyQuoted, EQUINE_REFERRED_INSURANCE_STATUS_ID } from './equine-premium-tbc.util';
import { requireEquineQuoteId, getMissingEquineHorseDetails, missingEquineHorseDetailsTab, resolveEquinePreviousPolicyId } from './equine-utils';

const EQUINE_IN_PROGRESS_STATUS_ID = 'e216564f-552a-4e45-ae67-0a63b7074691';

@Component({
  selector: 'app-equine',
  templateUrl: './equine.component.html',
  styleUrls: ['./equine.component.css']
})
export class EquineComponent implements OnInit {
  selectedIndex = 0;
  unlockedTabIndex = 0;
  staticData;
  modelObject: any = {};
  userDetailsLoaded = false;
  quoteType: number | null = null;

  constructor(
    private equineService: EquineService,
    private route: ActivatedRoute,
    private router: Router,
    private userInfoService: UserInfoService,
    private cd: ChangeDetectorRef,
    private snackBar: MatSnackBar
  ){}

  public ngOnInit() {
    this.quoteType = this.route.snapshot.params.quote_type != null
      ? +this.route.snapshot.params.quote_type
      : null;
    this.getEquineData();
    this.getStaticData();
  }

  public getStaticData() {
    this.equineService.getStaticData()
      .subscribe(
        (response: any) => {
          if (response.success) {
            this.staticData = response.result;
          }
        },
        (error) => {
          console.error(error);
        }
      );
  }

  private static readonly PREMIUM_SUMMARY_TAB_INDEX = 4;
  private static readonly ADDITIONAL_HORSE_TAB_INDEX = 1;

  public tabChanged(index: number) {
    if (index > this.unlockedTabIndex) {
      this.snackBar.open('Please save and continue before proceeding', 'Close', { duration: 3000 });
      this.cd.detectChanges();
      return;
    }
    if (index > 0 && !requireEquineQuoteId(this.modelObject)) {
      this.snackBar.open('Quote must be saved on Quote Details before continuing.', 'Close', { duration: 3000 });
      this.cd.detectChanges();
      return;
    }
    // Existing quotes unlock every tab, so Additional Horse Details can be skipped
    // entirely. Block the tab that quotes until its required fields are stored.
    if (index === EquineComponent.PREMIUM_SUMMARY_TAB_INDEX) {
      const missing = getMissingEquineHorseDetails(this.modelObject);
      if (missing.length) {
        const onQuoteDetails = missingEquineHorseDetailsTab(this.modelObject) === 0;
        this.snackBar.open(
          `Please complete ${missing.join(', ')} on ${onQuoteDetails ? 'Quote Details' : 'Additional Horse Details'} before continuing.`,
          'Close',
          { duration: 5000 }
        );
        this.selectedIndex = onQuoteDetails ? 0 : EquineComponent.ADDITIONAL_HORSE_TAB_INDEX;
        this.cd.detectChanges();
        return;
      }
    }
    this.selectedIndex = index;
    if (index === EquineComponent.PREMIUM_SUMMARY_TAB_INDEX) {
      setTimeout(() => this.scrollToTop());
    }
    this.cd.detectChanges();
  }

  public goToPremiumSummary(): void {
    if (!requireEquineQuoteId(this.modelObject)) {
      this.snackBar.open('Quote must be saved on Quote Details before continuing.', 'Close', { duration: 3000 });
      return;
    }
    this.unlockedTabIndex = Math.max(this.unlockedTabIndex, EquineComponent.PREMIUM_SUMMARY_TAB_INDEX);
    this.tabChanged(EquineComponent.PREMIUM_SUMMARY_TAB_INDEX);
  }

  /** Called when a tab's Save & Continue succeeds; move to the next tab. */
  public goToNextTab(): void {
    if (this.selectedIndex === 0 && !requireEquineQuoteId(this.modelObject)) {
      this.snackBar.open('Quote must be saved on Quote Details before continuing.', 'Close', { duration: 3000 });
      return;
    }
    const nextIndex = Math.min(this.selectedIndex + 1, EquineComponent.PREMIUM_SUMMARY_TAB_INDEX);
    this.unlockedTabIndex = Math.max(this.unlockedTabIndex, nextIndex);
    this.tabChanged(nextIndex);
  }

  /** Go to the previous tab (e.g. Back button). */
  public goToPrevTab(): void {
    this.tabChanged(Math.max(0, this.selectedIndex - 1));
  }

  private scrollToTop(): void {
    window.scrollTo(0, 0);
    document.documentElement.scrollTop = 0;
    document.body.scrollTop = 0;
    document.querySelectorAll('.main-panel, .content-wrapper, .page-body-wrapper').forEach((el) => {
      (el as HTMLElement).scrollTop = 0;
    });
  }

  public getEquineData(id = '') {
    const quote_id = this.route.snapshot.params.id ?? id ?? null;
    const quote_type = this.route.snapshot.params.quote_type != null
      ? +this.route.snapshot.params.quote_type
      : null;

    if (!quote_id) {
      this.modelObject = {};
      return;
    }

    const isMtaOrCancel = quote_type === 3 || quote_type === 4;
    if (!isMtaOrCancel) {
      this.unlockedTabIndex = EquineComponent.PREMIUM_SUMMARY_TAB_INDEX;
    }

    const loadOptions = (quote_type === 3 || quote_type === 4)
      ? { previewType: quote_type }
      : undefined;

    this.equineService.getEquineData(quote_id, loadOptions)
      .subscribe(
        (response: any) => {
          if (response.success) {
            this.modelObject = normalizeEquineQuotedReferralState(response.result || {});
            // When opening for MTA (3) or Cancellation (4), set type and previous_policy_id so save creates MTA/Cancel
            if (quote_type === 3 || quote_type === 4) {
              this.modelObject.original_transaction_type = this.modelObject.type || this.modelObject.transaction_type || 1;
              this.modelObject.transaction_type = quote_type;
              this.modelObject.type = quote_type;
              this.modelObject.previous_policy_id = this.modelObject.id || quote_id;
            } else {
              const previousPolicyId = resolveEquinePreviousPolicyId(this.modelObject);
              if (previousPolicyId) {
                this.modelObject.previous_policy_id = previousPolicyId;
              }
            }
          }
        },
        (error) => {
          console.error(error);
        }
      );
  }

  modelObjectUpdated(data) {
    const prev = this.modelObject || {};
    const wasPreviouslyQuoted = wasEquineQuotePreviouslyQuoted(prev)
      || wasEquineQuotePreviouslyQuoted(data);
    let merged = normalizeEquineQuotedReferralState({
      ...data,
      ...(wasPreviouslyQuoted ? { was_previously_quoted: true } : {}),
    });

    const prevReferred = prev.insurance_status_id === EQUINE_REFERRED_INSURANCE_STATUS_ID;
    const incomingReferred = merged.insurance_status_id === EQUINE_REFERRED_INSURANCE_STATUS_ID;
    const incomingInProgress = merged.insurance_status_id === EQUINE_IN_PROGRESS_STATUS_ID;
    const prevHadReferredReasons = Array.isArray(prev.referred_reasons) && prev.referred_reasons.length > 0;
    const incomingHadReferredReasons = Array.isArray(merged.referred_reasons) && merged.referred_reasons.length > 0;
    const sameQuote = !!prev.id && prev.id === merged.id;

    if (
      sameQuote &&
      prevReferred &&
      incomingInProgress &&
      !incomingHadReferredReasons &&
      (prevHadReferredReasons || prev.is_manually_referred)
    ) {
      merged = {
        ...merged,
        insurance_status_id: EQUINE_REFERRED_INSURANCE_STATUS_ID,
        referred_reasons: prev.referred_reasons ?? merged.referred_reasons,
        is_manually_referred: merged.is_manually_referred ?? prev.is_manually_referred,
        manually_referred_reason: merged.manually_referred_reason || prev.manually_referred_reason,
      };
    } else if (incomingReferred || incomingHadReferredReasons) {
      merged = {
        ...merged,
        referred_reasons: merged.referred_reasons ?? (sameQuote ? prev.referred_reasons : []),
      };
    }

    const previousPolicyId = resolveEquinePreviousPolicyId({
      ...prev,
      ...merged,
      previous_policy_id: merged.previous_policy_id || prev.previous_policy_id,
      old_policy_id: merged.old_policy_id || prev.old_policy_id,
    });
    this.modelObject = previousPolicyId
      ? { ...merged, previous_policy_id: previousPolicyId }
      : merged;
    // Do not refetch here: the emitted data is already the updated quote (e.g. from save/premium update).
    // Refetching caused ngOnChanges → patchFormValues → valueChanges → requestPremiumUpdate → loop.
  }
}
